import React from 'react';
import propTypes from 'prop-types';
import Select from 'react-select';
import debounce from 'debounce-promise';
import api from '../../../../utils/Api';
import { AUTH_TOKEN_KEY } from '../../../../constants';

/**
 * Label shown for a variant option when labelWithSku is on: display name first,
 * SKU in parentheses. Exported so callers that prefill a saved value (edit forms)
 * render the exact same label as the async options.
 */
export const variantOptionLabel = variant => {
  if(!variant) return '';
  const { display_name, SKU } = variant;
  return [display_name, SKU ? `(${SKU})` : null].filter(Boolean).join(' ') || SKU || '';
}

class SelectVariant extends React.Component{
  constructor(props){
    super(props);
    this.loadSelectOptions = this.loadSelectOptions.bind(this);
    this._getVariantOptions = this._getVariantOptions.bind(this);
    // Debounce keystrokes so we hit the API once the user pauses typing,
    // not on every character (mirrors SelectUser). Returns a promise, which
    // is what react-select's Async loadOptions expects.
    this._debouncedGetVariantOptions = debounce(this._getVariantOptions, 500);
  }

  cache = {}

  componentWillUnmount() {
    if (this._debouncedGetVariantOptions.cancel) {
      this._debouncedGetVariantOptions.cancel();
    }
  }

  loadSelectOptions(input){
    return this.refs[this.props.id].loadOptions(input);
  }

  purgeCache = () => {
    Object.keys(this.cache).forEach(entry => {
      delete this.cache[entry]
    })
  }

  _getVariantOptions(input){
    const myToken = localStorage.getItem(AUTH_TOKEN_KEY);
    const headers = {
      'Authorization': `Bearer ${myToken}`
    };
    const params = {
      keyword: input,
      active: 1,
      ...this.props.paramsApi
    }
    return api(headers).get(this.props.endpoint, { params })
    .then(({ data }) => {
      const products = data.data.data.map(item=>{
        return {
          id: item.id,
          name: this.props.labelWithSku
            ? variantOptionLabel(item)
            : (this.props.searchBySku?item.SKU:item.display_name),
	        hide_box_info: item.hide_box_info,
          category: item.product.category.name,
        }
      })
      if(this.props.onSelectSearch){
        this.props.onSelectSearch(input)
      }
      return{ options: products };
    });
  }

  render(){
    return(
      <Select.Async
        valueKey="id"
        labelKey="name"
        ref={this.props.id}
        loadOptions={this._debouncedGetVariantOptions}
        /**
         * The endpoint already matches the keyword against display_name, SKU and brand.
         * react-select v1 re-filters those results against the label, which silently drops
         * every server match the label does not happen to contain (e.g. a brand hit).
         * Keep the server as the only filter.
         */
        filterOptions={this.props.labelWithSku ? options => options : undefined}
        cache={this.cache}
        id={this.props.id}
        name={this.props.name}
        value={this.props.value}
        placeholder={this.props.placeholder}
        noResultsText={this.props.noResultsText}
        onChange={this.props.onSelectChange}
        onOpen={this.props.onSelectOpen}
        multi={this.props.isMulti}
        disabled={this.props.disabled}
      />
    )
  }
}

export default SelectVariant;

SelectVariant.defaultProps = {
  cacheOptions: false,
  searchBySku: false,
  labelWithSku: false,
  disabled: false,
  value: null,
  placeholder: 'Select a product...',
  noResultsText: 'No Result!',
  paramsApi: {},
  endpoint: '/admins/productvariants'
}

SelectVariant.propTypes = {
  id: propTypes.string.isRequired,
  name: propTypes.string,
  value: propTypes.object,
  placeholder: propTypes.string,
  noResultsText: propTypes.string,
  cacheOptions: propTypes.bool,
  searchBySku: propTypes.bool,
  labelWithSku: propTypes.bool,
  disabled: propTypes.bool,
  paramsApi: propTypes.object,
  endpoint: propTypes.string,
  onSelectSearch: propTypes.func,
  onSelectChange: propTypes.func,
  onSelectOpen: propTypes.func
}
